import { useState, useRef, useCallback, useEffect } from 'react'

import type { CustomGridBreakpointKey, InsightCardWidth } from '@/types/dashboard-types'
import { responsiveConfig } from '@/utils/responsive'

import { type CustomGridLayoutProps, type LayoutItem, COLS } from '../CustomGridLayout'

export function useGridLayout(
  cards: CustomGridLayoutProps['cards'],
  isEditMode: boolean,
  rowHeight: number,
  onLayoutChange?: CustomGridLayoutProps['onLayoutChange'],
  onUnsavedChangesChange?: (hasUnsaved: boolean) => void,
  editingBreakpoint: CustomGridBreakpointKey | null = null,
  currentBreakpoint: CustomGridBreakpointKey = 'lg',
  setUnsavedBreakpoint?: (breakpoint: CustomGridBreakpointKey | null) => void
) {
  function cardSizeToColumns(size: InsightCardWidth, breakpoint: CustomGridBreakpointKey): number {
    const config = responsiveConfig[breakpoint]
    return config[size]
  }

  function calculateHeightInRows(
    breakpoint: CustomGridBreakpointKey,
    rowHeight: number,
    override?: Partial<Record<CustomGridBreakpointKey, number>>
  ): number {
    const defaultHeights: Record<CustomGridBreakpointKey, number> = {
      lg: 400,
      md: 300,
      sm: 250,
    }

    return Math.ceil((override?.[breakpoint] ?? defaultHeights[breakpoint]) / rowHeight)
  }

  function getMinConstraints(breakpoint: CustomGridBreakpointKey) {
    const constraints = {
      lg: { minW: 4, minH: 3 },
      md: { minW: 3, minH: 3 },
      sm: { minW: 6, minH: 4 },
    }
    return constraints[breakpoint]
  }

  function overlaps(a: { x: number; y: number; w: number; h: number }, b: LayoutItem): boolean {
    return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  }

  function findFreeSlot(placed: LayoutItem[], w: number, h: number, cols: number): { x: number; y: number } {
    const width = Math.min(w, cols)
    const maxY = placed.reduce((acc, item) => Math.max(acc, item.y + item.h), 0)

    for (let y = 0; y <= maxY; y++) {
      for (let x = 0; x + width <= cols; x++) {
        if (!placed.some((item) => overlaps({ x, y, w: width, h }, item))) {
          return { x, y }
        }
      }
    }
    return { x: 0, y: maxY }
  }

  function compactVertically(items: LayoutItem[]): LayoutItem[] {
    const sorted = [...items].sort((a, b) => a.y - b.y || a.x - b.x)
    const settled: LayoutItem[] = []

    for (const item of sorted) {
      let y = item.y
      while (y > 0 && !settled.some((s) => overlaps({ ...item, y: y - 1 }, s))) {
        y--
      }
      settled.push({ ...item, y })
    }

    return settled
  }

  function cardsToLayout(
    cards: CustomGridLayoutProps['cards'],
    breakpoint: CustomGridBreakpointKey,
    rowHeight: number
  ): LayoutItem[] {
    const cols = COLS[breakpoint]
    const { minW, minH } = getMinConstraints(breakpoint)

    const saved: LayoutItem[] = []
    const unplaced: CustomGridLayoutProps['cards'] = []

    for (const card of cards) {
      const override = card.config.position?.[breakpoint]
      if (override) {
        saved.push({ i: card.id, x: override.x, y: override.y, w: override.w, h: override.h, minW, minH })
      } else {
        unplaced.push(card)
      }
    }

    const placed = compactVertically(saved)

    for (const card of unplaced) {
      const w = Math.min(cardSizeToColumns(card.config.width, breakpoint), cols)
      const h = calculateHeightInRows(breakpoint, rowHeight, card.config.height)
      const { x, y } = findFreeSlot(placed, w, h, cols)

      placed.push({ i: card.id, x, y, w, h, minW, minH })
    }

    const compacted = compactVertically(placed)

    return cards.map((card) => compacted.find((item) => item.i === card.id)).filter((item) => item !== undefined)
  }

  function hasChanged(oldItem: LayoutItem | undefined, newItem: LayoutItem): boolean {
    if (!oldItem) return true
    return oldItem.x !== newItem.x || oldItem.y !== newItem.y || oldItem.w !== newItem.w || oldItem.h !== newItem.h
  }

  const isInitialMount = useRef(true)
  const [mergedCards, setMergedCards] = useState<CustomGridLayoutProps['cards']>(cards)

  // Effect 1: Discard unsaved changes when exiting edit mode
  useEffect(() => {
    if (!isEditMode && setUnsavedBreakpoint) {
      setMergedCards(cards)
    }
  }, [isEditMode, cards])

  // Effect 2: Sync cards when they change (add/remove insights)
  useEffect(() => {
    setMergedCards((prev) => {
      const prevMap = new Map(prev.map((c) => [c.id, c]))

      return cards.map((card) => {
        const existing = prevMap.get(card.id)
        if (existing?.config.position) {
          return {
            ...card,
            config: {
              ...card.config,
              position: existing.config.position,
            },
          }
        }

        return card
      })
    })
  }, [cards])

  useEffect(() => {
    if (!isEditMode) return

    const activeBreakpoint: CustomGridBreakpointKey = editingBreakpoint || currentBreakpoint
    if (!mergedCards.some((card) => !card.config.position?.[activeBreakpoint])) return

    const layout = cardsToLayout(mergedCards, activeBreakpoint, rowHeight)
    const updated = mergedCards.map((card) => {
      // Only commit cards that have no saved position yet — rewriting the rest would
      // clobber a drag in progress with freshly compacted coordinates.
      if (card.config.position?.[activeBreakpoint]) return card

      const item = layout.find((l) => l.i === card.id)
      if (!item) return card

      return {
        ...card,
        config: {
          ...card.config,
          position: {
            ...card.config.position,
            [activeBreakpoint]: { x: item.x, y: item.y, w: item.w, h: item.h },
          },
        },
      }
    })

    setMergedCards(updated)
    setUnsavedBreakpoint?.(activeBreakpoint)
    onUnsavedChangesChange?.(true)
    onLayoutChange?.(updated)
  }, [mergedCards, isEditMode, editingBreakpoint, currentBreakpoint, rowHeight])

  // Handle layout changes from drag/resize
  const handleLayoutChange = useCallback(
    (currentLayout: LayoutItem[], fromUserInteraction: boolean = false) => {
      if (isInitialMount.current) {
        isInitialMount.current = false
        return
      }

      if (!onLayoutChange) return
      if (!isEditMode) return

      const activeBreakpoint: CustomGridBreakpointKey = editingBreakpoint || currentBreakpoint
      const oldLayout = cardsToLayout(mergedCards, activeBreakpoint, rowHeight)

      let hasAnyChanges = false
      const updatedCards = mergedCards.map((card) => {
        const newItem = currentLayout.find((item) => item.i === card.id)
        if (!newItem) return card

        const oldItem = oldLayout.find((item) => item.i === card.id)

        if (!hasChanged(oldItem, newItem)) {
          return card
        }

        hasAnyChanges = true
        const breakpointsToSave: CustomGridBreakpointKey = editingBreakpoint || currentBreakpoint
        return {
          ...card,
          config: {
            ...card.config,
            position: {
              ...card.config.position,
              [breakpointsToSave]: {
                x: newItem.x,
                y: newItem.y,
                w: newItem.w,
                h: newItem.h,
              },
            },
          },
        }
      })

      if (hasAnyChanges && fromUserInteraction) {
        const activeBreakpoint: CustomGridBreakpointKey = editingBreakpoint || currentBreakpoint
        setUnsavedBreakpoint?.(activeBreakpoint)
        onUnsavedChangesChange?.(true)
        setMergedCards(updatedCards)
        onLayoutChange(updatedCards)
      }
    },
    [
      mergedCards,
      currentBreakpoint,
      onLayoutChange,
      editingBreakpoint,
      isEditMode,
      setUnsavedBreakpoint,
      onUnsavedChangesChange,
      rowHeight,
    ]
  )

  const layouts = editingBreakpoint
    ? {
        [editingBreakpoint]: cardsToLayout(mergedCards, editingBreakpoint, rowHeight),
      }
    : {
        lg: cardsToLayout(mergedCards, 'lg', rowHeight),
        md: cardsToLayout(mergedCards, 'md', rowHeight),
        sm: cardsToLayout(mergedCards, 'sm', rowHeight),
      }

  return {
    mergedCards,
    setMergedCards,
    layouts,
    handleLayoutChange,
  }
}
